import type { ReactNode } from 'react'
import { formatAmount } from '../lib/format'

/**
 * Champs de saisie. Peu de champs par ecran, etiquettes en clair, et
 * chaque champ fait 54 px de haut avec un texte a 16 px pour qu'Android
 * ne zoome pas tout seul a la saisie.
 */

interface BaseProps {
  label: string
  hint?: string
  required?: boolean
  error?: string | null
}

function Wrapper({
  label, hint, required, error, children,
}: BaseProps & { children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        {required && <span className="field__required"> *</span>}
      </span>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error">{error}</span>}
    </label>
  )
}

export function TextField({
  value, onChange, placeholder, autoCapitalize = 'words', ...base
}: BaseProps & {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoCapitalize?: 'none' | 'words' | 'sentences'
}) {
  return (
    <Wrapper {...base}>
      <input
        className="field__input"
        type="text"
        value={value}
        placeholder={placeholder}
        autoCapitalize={autoCapitalize}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrapper>
  )
}

export function TextAreaField({
  value, onChange, rows = 3, ...base
}: BaseProps & { value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <Wrapper {...base}>
      <textarea
        className="field__input field__input--area"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrapper>
  )
}

export function PhoneField({
  value, onChange, ...base
}: BaseProps & { value: string; onChange: (v: string) => void }) {
  return (
    <Wrapper {...base}>
      <input
        className="field__input"
        type="tel"
        inputMode="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrapper>
  )
}

/**
 * Montant en francs CFA entiers.
 *
 * Volontairement un champ texte filtre aux chiffres, et non un
 * input[type=number] : le champ numerique affiche des fleches inutiles
 * au doigt, accepte une virgule decimale selon la langue du telephone,
 * et laisse taper « 1e5 ». Ici seuls les chiffres entrent, et le
 * groupement par milliers s'affiche pendant la frappe.
 */
export function MoneyField({
  value, onChange, ...base
}: BaseProps & { value: number | null; onChange: (v: number | null) => void }) {
  const affiche = value === null ? '' : formatAmount(value)

  return (
    <Wrapper {...base} hint={base.hint ?? 'F CFA'}>
      <div className="field__money">
        <input
          className="field__input"
          type="text"
          inputMode="numeric"
          value={affiche}
          onChange={(e) => {
            const chiffres = e.target.value.replace(/[^0-9]/g, '')
            onChange(chiffres === '' ? null : Number(chiffres))
          }}
        />
        <span className="field__suffix">F CFA</span>
      </div>
    </Wrapper>
  )
}

/**
 * Date. On utilise input[type=date] pour obtenir le selecteur natif
 * d'Android, bien plus sur au doigt qu'une saisie libre. La valeur
 * echangee reste au format AAAA-MM-JJ, celui de la base ; l'affichage
 * en JJ/MM/AAAA est fait par formatDate partout ailleurs.
 */
export function DateField({
  value, onChange, ...base
}: BaseProps & { value: string; onChange: (v: string) => void }) {
  return (
    <Wrapper {...base}>
      <input
        className="field__input"
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrapper>
  )
}

export function SelectField<T extends string>({
  value, onChange, options, ...base
}: BaseProps & {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return (
    <Wrapper {...base}>
      <select
        className="field__input field__input--select"
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Wrapper>
  )
}

export function CheckField({
  value, onChange, label, hint,
}: {
  value: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <label className="checkfield">
      <input
        type="checkbox"
        className="checkfield__box"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="checkfield__label">{label}</span>
        {hint && <span className="field__hint">{hint}</span>}
      </span>
    </label>
  )
}
